import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldAlert, Search } from "lucide-react";

/**
 * Phase 22 — Impersonation audit log.
 *
 * Each ``platform_admin_impersonation`` audit event surfaces here with
 * the actor identity, the tenant that was entered, and the tenant they
 * came from (if any). One row is emitted per ``switch-tenant`` call.
 */
export default function PlatformImpersonationLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/impersonation-log", { params: { limit: 200 } });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (r.actor?.email || "").toLowerCase().includes(s) ||
      (r.tenant?.slug || "").toLowerCase().includes(s) ||
      (r.tenant?.name || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-4" data-testid="platform-impersonation-page">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by admin email or workspace…"
            className="pl-8"
            data-testid="platform-impersonation-search"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} of {items.length} events
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Viewing workspace</TableHead>
                <TableHead>From workspace</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading events…
                </TableCell></TableRow>
              )}
              {!loading && filtered.map((r, i) => {
                const from = (r.metadata || {}).from_tenant_id;
                return (
                  <TableRow key={(r.id || i) + "-" + i} data-testid={`impersonation-row-${i}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <ShieldAlert className="h-3.5 w-3.5 text-secondary" />
                        <span className="text-sm">{r.actor?.email || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-primary">{r.tenant?.name || "—"}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.tenant?.slug || ""}</div>
                    </TableCell>
                    <TableCell>
                      {from ? (
                        <Badge variant="outline" className="font-mono text-[10px]">{from.slice(0, 8)}…</Badge>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">{r.ip || "—"}</TableCell>
                  </TableRow>
                );
              })}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  {q ? "No view-as events match that search." : "Nothing here yet."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
